import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import type { Uuid } from '@lib/validators/params'

interface CancelTarget {
  tenantId: Uuid
  id: Uuid
}

export interface HandoffCancel<T> {
  /** The cancelled record, once this page's record was cancelled. */
  result: T | undefined
  /** Why cancelling this page's record failed, if it did. */
  error: Error | null
  isPending: boolean
  /** Cancel from the page's own button. */
  cancel: () => void
}

/**
 * Cancelling a checkout or card setup from PixelSign's handoff page: once
 * when Stripe's back link returns with `?cancelled=1`, or when the person
 * presses Cancel. The outcome belongs to the record it was sent for, so a
 * page reused for another record neither shows it nor skips that record's
 * own cancel.
 */
export function useHandoffCancel<T>(
  tenantId: Uuid | null,
  id: Uuid | null,
  { returnedCancelled, signedIn, send }: { returnedCancelled: boolean; signedIn: boolean; send: (target: CancelTarget) => Promise<T> },
): HandoffCancel<T> {
  const mutation = useMutation({ mutationFn: send })
  const { mutate } = mutation
  const key = tenantId !== null && id !== null ? `${tenantId}:${id}` : null
  const sentForRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!returnedCancelled || !signedIn || tenantId === null || id === null || sentForRef.current === key) return
    sentForRef.current = key
    mutate({ tenantId, id })
  }, [returnedCancelled, signedIn, tenantId, id, key, mutate])

  const sent = mutation.variables
  const mine = sent !== undefined && `${sent.tenantId}:${sent.id}` === key
  return {
    result: mine ? mutation.data : undefined,
    error: mine ? mutation.error : null,
    isPending: mutation.isPending,
    cancel: () => {
      if (tenantId !== null && id !== null) mutate({ tenantId, id })
    },
  }
}
